import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, AlertTriangle, ShieldCheck, ClipboardList, History,
  Check, Loader2, Plus,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import DetailsSidebar from '../../components/DetailsSidebar';
import { Modal } from '../../components';
import { useNavigate } from 'react-router-dom';
import { reminderService } from '../../services';
import { reminderLink } from '../../utils/reminderLinks';

/**
 * Reminders widget + a "View all" off-canvas.
 *
 * Two modes:
 *  - Dashboard (org-wide): pass `reminders` (top-5 from the dashboard payload).
 *  - Resident page: pass `residenceId` and the card self-fetches that
 *    resident's reminders for both the inline list and the off-canvas.
 *
 * Reminders are produced automatically by the backend whenever a record with a
 * review / follow-up date is saved (care plan next-review, risk-assessment
 * re-assess date, incident follow-up).
 */

// ── Visual config per reminder type ──────────────────────────────────────────
const TYPE_META = {
  care_plan_review:       { Icon: History,       wrap: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400' },
  risk_assessment_review: { Icon: ShieldCheck,   wrap: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' },
  incident_followup:      { Icon: AlertTriangle, wrap: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400' },
  allergy_flag_review:    { Icon: AlertTriangle, wrap: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400' },
  custom:                 { Icon: ClipboardList, wrap: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' },
};
const metaFor = (type) => TYPE_META[type] || TYPE_META.custom;

// ── Due-date label + tone ────────────────────────────────────────────────────
const dueInfo = (dateStr) => {
  if (!dateStr) return { text: 'No date set', tone: 'muted' };
  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`, tone: 'overdue' };
  if (days === 0) return { text: 'Due today', tone: 'overdue' };
  return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, tone: 'due' };
};
const toneClass = {
  overdue: 'text-rose-600',
  due: 'text-orange-500',
  muted: 'text-[#94A3B8]',
};

// ── A single reminder row ────────────────────────────────────────────────────
const ReminderRow = ({ r, onComplete, completing, onOpen }) => {
  const { Icon, wrap } = metaFor(r.type);
  const due = dueInfo(r.due_date);
  const link = reminderLink(r);
  return (
    <div
      onClick={link ? () => onOpen(link) : undefined}
      role={link ? 'link' : undefined}
      title={link ? 'Open' : undefined}
      className={`flex items-center gap-3 bg-white dark:bg-[#262b33] rounded-xl border border-[#F1F5F9] dark:border-gray-800 shadow-sm px-3 py-2.5 ${link ? 'cursor-pointer hover:border-blue-200 dark:hover:border-blue-900/60 hover:bg-[#F8FAFF] dark:hover:bg-[#2c313a] transition-colors' : ''}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${wrap}`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100 leading-tight truncate">{r.title}</p>
        {r.description && (
          <p className="text-[11px] text-[#64748B] dark:text-gray-400 truncate mt-0.5">{r.description}</p>
        )}
        <p className={`text-[11px] font-semibold mt-0.5 ${toneClass[due.tone]}`}>{due.text}</p>
      </div>
      {onComplete && (
        <button
          // The row itself opens the reminder — ticking it must not.
          onClick={(e) => { e.stopPropagation(); onComplete(r); }}
          disabled={completing}
          title="Mark as done"
          className="w-8 h-8 rounded-lg border border-[#E2E8F0] dark:border-gray-700 flex items-center justify-center text-[#64748B] dark:text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition shrink-0 disabled:opacity-50"
        >
          {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
};

// ── Compact due meta for the inline dashboard rows (label + urgency chip) ─────
const dueMeta = (dateStr) => {
  if (!dateStr) return { text: 'No date set', chip: 'Someday', tone: 'muted' };
  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) { const n = Math.abs(days); return { text: `${n} day${n === 1 ? '' : 's'} overdue`, chip: 'Overdue', tone: 'overdue' }; }
  if (days === 0) return { text: 'Due today', chip: 'Today', tone: 'today' };
  return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, chip: `${days}d`, tone: 'due' };
};

// Flag-tile + chip colours keyed to urgency (matches the dashboard concept).
const INLINE_TONE = {
  overdue: { flag: 'bg-[#FFF1F3] dark:bg-red-900/20 text-[#E11D48] dark:text-red-400 ring-[#FFDCE3] dark:ring-red-900/50', chip: 'text-[#E11D48] dark:text-red-400 bg-[#FFF1F3] dark:bg-red-900/20' },
  today:   { flag: 'bg-[#FFF4ED] dark:bg-orange-900/20 text-[#EA580C] dark:text-orange-400 ring-[#FCDDC8] dark:ring-orange-900/50', chip: 'text-[#EA580C] dark:text-orange-400 bg-[#FFF4ED] dark:bg-orange-900/20' },
  due:     { flag: 'bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 ring-[#D8E4FF] dark:ring-blue-900/50', chip: 'text-[#2563EB] dark:text-blue-400 bg-[#EEF3FF] dark:bg-blue-900/20' },
  muted:   { flag: 'bg-[#F4F7FC] dark:bg-[#31373f] text-[#64748B] dark:text-gray-400 ring-[#E3E9F3] dark:ring-gray-700', chip: 'text-[#64748B] dark:text-gray-400 bg-[#F4F7FC] dark:bg-[#31373f]' },
};

// Inline dashboard row: flag tile + title/subtitle + urgency chip (divided list).
const InlineReminderRow = ({ r, onOpen }) => {
  const { Icon } = metaFor(r.type);
  const meta = dueMeta(r.due_date);
  const tone = INLINE_TONE[meta.tone] || INLINE_TONE.muted;
  const link = reminderLink(r);
  return (
    <div
      onClick={link ? () => onOpen(link) : undefined}
      role={link ? 'link' : undefined}
      title={link ? 'Open' : undefined}
      className={`flex items-start gap-3 px-[22px] py-3 border-t border-[#EEF2F8] dark:border-gray-800 hover:bg-[#F4F7FC] dark:hover:bg-[#222] transition-colors ${link ? 'cursor-pointer' : ''}`}
    >
      <span className={`w-8 h-8 rounded-[10px] grid place-items-center shrink-0 ring-1 ring-inset ${tone.flag}`}>
        <Icon className="w-4 h-4" strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 leading-[1.3] truncate">{r.title}</p>
        <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 truncate mt-0.5">{r.description || meta.text}</p>
      </div>
      <span className={`shrink-0 text-[10px] font-bold px-2 py-[3px] rounded-[7px] whitespace-nowrap ${tone.chip}`}>
        {meta.chip}
      </span>
    </div>
  );
};

const RemindersCard = ({ reminders, residenceId, onChanged, title = 'Reminders' }) => {
  const selfFetch = reminders === undefined; // resident-page mode
  const navigate = useNavigate();
  // Click a reminder → the record it is about (see utils/reminderLinks.js).
  const openReminder = (link) => {
    setOpen(false);
    navigate(link);
  };

  const [open, setOpen] = useState(false);
  const [inlineReminders, setInlineReminders] = useState(reminders || []);
  const [inlineLoading, setInlineLoading] = useState(selfFetch);
  const [allReminders, setAllReminders] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  // Add-reminder modal
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_date: '' });

  // Keep inline list in sync with the prop when used in dashboard mode
  useEffect(() => {
    if (!selfFetch) setInlineReminders(reminders || []);
  }, [reminders, selfFetch]);

  const fetchInline = useCallback(async () => {
    if (!selfFetch) return;
    setInlineLoading(true);
    try {
      const params = { status: 'pending', limit: 5 };
      if (residenceId) params.residence_id = residenceId;
      const res = await reminderService.getReminders(params);
      if (res.success) setInlineReminders(res.data || []);
    } catch (_) {
      // non-fatal — the widget just stays empty
    } finally {
      setInlineLoading(false);
    }
  }, [selfFetch, residenceId]);

  useEffect(() => { fetchInline(); }, [fetchInline]);

  const openAll = async () => {
    setOpen(true);
    setLoadingAll(true);
    try {
      const params = { status: 'pending' };
      if (residenceId) params.residence_id = residenceId;
      const res = await reminderService.getReminders(params);
      if (res.success) setAllReminders(res.data || []);
    } catch (_) {
      toast.error('Could not load reminders');
    } finally {
      setLoadingAll(false);
    }
  };

  const handleComplete = async (r) => {
    setCompletingId(r.id);
    try {
      await reminderService.completeReminder(r.id);
      toast.success('Reminder marked done');
      setAllReminders((prev) => prev.filter((x) => x.id !== r.id));
      if (selfFetch) fetchInline();
      if (onChanged) onChanged();
    } catch (_) {
      toast.error('Could not update reminder');
    } finally {
      setCompletingId(null);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      await reminderService.createReminder({
        type: 'custom',
        title: form.title.trim(),
        description: form.description.trim() || null,
        due_date: form.due_date || null,
        ...(residenceId ? { residence_id: residenceId } : {}),
      });
      toast.success('Reminder added');
      setAddOpen(false);
      setForm({ title: '', description: '', due_date: '' });
      if (selfFetch) fetchInline();
      if (open) openAll();
      if (onChanged) onChanged();
    } catch (_) {
      toast.error('Could not add reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ── Widget card ── */}
      <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]">
        <div className="flex items-center justify-between px-[22px] pt-[18px] pb-1.5">
          <h3 className="flex items-center gap-[11px] text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
            <span className="w-1 h-[18px] rounded bg-gradient-to-b from-rose-500 to-rose-600" />
            {title}
            {inlineReminders.length > 0 && (
              <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-[#FFF1F3] dark:bg-red-900/20 text-[#E11D48] dark:text-red-400">
                {inlineReminders.length} due
              </span>
            )}
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1 text-[12.5px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
            <button
              onClick={openAll}
              className="text-[12.5px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700"
            >
              View all
            </button>
          </div>
        </div>

        {inlineLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          </div>
        ) : inlineReminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-[22px]">
            <Bell className="w-7 h-7 text-[#CBD5E1] mb-2" />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic">No reminders due — all caught up.</p>
          </div>
        ) : (
          <div className="pb-1">
            {inlineReminders.map((r) => <InlineReminderRow key={r.id} r={r} onOpen={openReminder} />)}
          </div>
        )}
      </div>

      {/* ── View-all off-canvas ── */}
      <DetailsSidebar
        isOpen={open}
        onClose={() => setOpen(false)}
        title={title}
        icon={Bell}
        maxWidth="max-w-[440px]"
      >
        {loadingAll ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
          </div>
        ) : allReminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bell className="w-9 h-9 text-[#CBD5E1] mb-3" />
            <p className="text-[13px] font-medium text-[#64748B] dark:text-gray-400">No pending reminders</p>
            <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1 max-w-[240px]">
              Review and follow-up reminders appear here automatically when you set dates on care plans, risk assessments and incidents.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {allReminders.map((r) => (
              <ReminderRow key={r.id} r={r} onComplete={handleComplete} completing={completingId === r.id} onOpen={openReminder} />
            ))}
          </div>
        )}
      </DetailsSidebar>

      {/* ── Add reminder modal ── */}
      <Modal
        isOpen={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        title="Add reminder"
        maxWidth="max-w-md"
        closeOnBackdrop
      >
        <form onSubmit={handleAddReminder} className="space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Title <span className="text-rose-500">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Call GP about medication"
              autoFocus
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Optional details"
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Due date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={saving}
              className="px-4 py-2 text-[13px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2c313a] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-[13px] font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:bg-blue-400 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Add reminder'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default RemindersCard;
